import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "motion/react";
import { 
  ChevronLeft, Send, Loader2, MessageCircle, 
  Clock, RefreshCw
} from "lucide-react";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "../../contexts/AuthContext";
import { Section, Button } from "./PageComponents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Individual conversation view
export default function MessagesPage({ connectionId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [otherProfile, setOtherProfile] = useState(null);
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/messages/conversation/${connectionId}`,
        { withCredentials: true }
      );
      setMessages(data.messages);
      setError(null);
    } catch (err) {
      if (!loading) { // Don't show error on initial load
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [connectionId, loading]);

  const fetchConnectionInfo = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/penpal/connections`, { withCredentials: true });
      const conn = data.connections.find(c => c.id === connectionId);
      if (conn?.other_profile) {
        setOtherProfile(conn.other_profile);
      }
    } catch (err) {
      console.error("Failed to fetch connection info:", err);
    }
  }, [connectionId]);

  useEffect(() => {
    if (connectionId && user?.penpal_profile_id) {
      fetchMessages();
      fetchConnectionInfo();
      
      // Poll for new messages every 30 seconds
      pollInterval.current = setInterval(fetchMessages, 30000);
      
      return () => {
        if (pollInterval.current) {
          clearInterval(pollInterval.current);
        }
      };
    }
  }, [connectionId, user, fetchMessages, fetchConnectionInfo]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    setError(null);

    try {
      await axios.post(`${BACKEND_URL}/api/messages/send`, {
        connection_id: connectionId,
        content: newMessage.trim()
      }, { withCredentials: true });
      
      setNewMessage("");
      await fetchMessages();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  if (!user) {
    return (
      <Section>
        <div className="text-center">
          <h2 className="text-3xl font-serif mb-4">Please sign in first</h2>
          <Button href="#login">Sign In</Button>
        </div>
      </Section>
    );
  }

  if (!connectionId) {
    return (
      <Section>
        <div className="text-center">
          <h2 className="text-3xl font-serif mb-4">No conversation selected</h2>
          <Button href="#penpal-connections">View Connections</Button>
        </div>
      </Section>
    );
  }

  return (
    <Section id="penpal-messages">
      <div className="max-w-3xl mx-auto" data-testid="messages-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-6 border-b border-lws-ink/5">
          <div className="flex items-center gap-4">
            <a 
              href="#penpal-connections" 
              className="p-2 hover:bg-lws-taupe/10 transition-colors"
            >
              <ChevronLeft size={24} />
            </a>
            <div>
              <h1 className="text-2xl font-serif text-lws-green">
                {otherProfile?.display_name || "Loading..."}
              </h1>
              {otherProfile?.country && (
                <p className="text-sm text-lws-taupe">{otherProfile.country}</p>
              )}
            </div>
          </div>
          
          <button
            onClick={fetchMessages}
            className="p-2 text-lws-taupe hover:text-lws-green transition-colors"
            title="Refresh messages"
          >
            <RefreshCw size={20} />
          </button>
        </div>

        {/* Messages Container */}
        <div className="bg-lws-taupe/5 min-h-[400px] max-h-[500px] overflow-y-auto p-6 mb-6" data-testid="messages-container">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 size={32} className="animate-spin text-lws-green" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-16">
              <MessageCircle size={48} className="text-lws-taupe/30 mx-auto mb-4" />
              <p className="text-lws-taupe">No messages yet</p>
              <p className="text-sm text-lws-taupe/60 mt-2">Start the conversation with a thoughtful message.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, idx) => {
                const isMine = msg.sender_profile_id === user.penpal_profile_id;
                const showDate = idx === 0 || 
                  new Date(msg.created_at).toDateString() !== new Date(messages[idx-1].created_at).toDateString();
                
                return (
                  <div key={msg.id}>
                    {showDate && (
                      <div className="text-center my-6">
                        <span className="text-[10px] uppercase tracking-widest text-lws-taupe bg-lws-paper px-4 py-1">
                          {new Date(msg.created_at).toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </span>
                      </div>
                    )}
                    
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] ${isMine ? 'bg-lws-green text-lws-paper' : 'bg-white border border-lws-ink/10'} p-4`}>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-[10px] mt-2 ${isMine ? 'text-lws-paper/60' : 'text-lws-taupe'}`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </motion.div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 text-red-600 p-4 mb-4 text-sm">
            {error}
          </div>
        )}

        {/* Message Input */}
        <form onSubmit={handleSend} className="flex gap-4" data-testid="message-form">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Write your message..."
            rows={3}
            className="flex-1 border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green resize-none"
            data-testid="message-input"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.metaKey) {
                handleSend(e);
              }
            }}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="self-end px-6 py-3 bg-lws-green text-lws-paper hover:bg-lws-navy transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="send-btn"
          >
            {sending ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <>
                <Send size={20} />
                Send
              </>
            )}
          </button>
        </form>
        
        <p className="text-xs text-lws-taupe/60 mt-4 text-center">
          Press ⌘+Enter to send • Messages are stored securely
        </p>
      </div>
    </Section>
  );
}

// Conversations list component
export function ConversationsListPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.penpal_profile_id) {
      fetchConversations();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchConversations = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/messages/conversations`, { withCredentials: true });
      setConversations(data.conversations);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  if (!user) {
    return (
      <Section>
        <div className="text-center">
          <h2 className="text-3xl font-serif mb-4">Please sign in first</h2>
          <Button href="#login">Sign In</Button>
        </div>
      </Section>
    );
  }

  if (!user.penpal_profile_id) {
    return (
      <Section>
        <div className="text-center">
          <h2 className="text-3xl font-serif mb-4">No Profile Found</h2>
          <p className="text-lws-taupe mb-8">Create your pen pal profile to start messaging.</p>
          <Button href="#penpal-create">Create Profile</Button>
        </div>
      </Section>
    );
  }

  return (
    <Section id="penpal-inbox">
      <div className="max-w-2xl mx-auto" data-testid="inbox-page">
        <a href="#penpal-connections" className="inline-flex items-center text-xs uppercase tracking-widest text-lws-taupe hover:text-lws-green mb-8 transition-colors">
          <ChevronLeft size={16} className="mr-2" /> Back to Connections
        </a>
        
        <h1 className="text-4xl md:text-5xl font-serif text-lws-green mb-8">Messages</h1>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 mb-8">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-lws-green" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-16 bg-lws-taupe/5">
            <MessageCircle size={48} className="text-lws-taupe/30 mx-auto mb-4" />
            <p className="text-xl text-lws-taupe mb-4">No conversations yet</p>
            <p className="text-sm text-lws-taupe mb-8">Find a pen pal match to start exchanging messages!</p>
            <Button href="#penpal-browse">Browse Pen Pals</Button>
          </div>
        ) : (
          <div className="space-y-2" data-testid="conversations-list">
            {conversations.map((conv) => (
              <a
                key={conv.connection_id}
                href={`#penpal-messages/${conv.connection_id}`}
                className="block border border-lws-ink/5 p-4 hover:border-lws-green/30 hover:bg-lws-taupe/5 transition-all"
                data-testid={`conversation-${conv.connection_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-serif text-lg truncate">
                        {conv.other_profile?.display_name || "Unknown"}
                      </h3>
                      {conv.unread_count > 0 && (
                        <span className="bg-lws-amber text-lws-paper text-[10px] px-2 py-0.5 rounded-full font-bold">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    {conv.last_message ? (
                      <p className={`text-sm truncate mt-1 ${conv.unread_count > 0 ? 'text-lws-ink font-medium' : 'text-lws-taupe'}`}>
                        {conv.last_message.is_mine && <span className="text-lws-taupe">You: </span>}
                        {conv.last_message.content}
                      </p>
                    ) : (
                      <p className="text-sm text-lws-taupe/60 italic mt-1">No messages yet</p>
                    )}
                  </div>
                  
                  {conv.last_message?.created_at && (
                    <span className="text-xs text-lws-taupe ml-4 flex-shrink-0">
                      {formatTime(conv.last_message.created_at)}
                    </span>
                  )}
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}
